import React, { useEffect, useRef } from 'react';
import './ChatbotLogo.css';

export default function ChatbotLogo({ inverted = false, size = 180 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      ctx.scale(dpr, dpr);
    };

    // Check for dark mode
    const isDarkMode = () => {
      return document.documentElement.getAttribute('data-theme') === 'dark';
    };

    const drawWavyLines = () => {
      const width = size;
      const height = size;
      const dark = isDarkMode();
      
      ctx.clearRect(0, 0, width, height);
      
      // Create circular clipping path
      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
      ctx.clip();
      
      // Use light blue in dark mode, dark blue in light mode (inverted prop takes precedence for explicit inversion)
      const strokeColor = (inverted || dark) ? (dark ? 'rgba(96, 165, 250, 0.6)' : 'rgba(255, 255, 255, 0.4)') : 'rgba(2, 45, 84, 0.4)';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      
      const numLines = 12; // Fewer lines for more spacing
      const curvature = -0.12;
      
      for (let i = 0; i <= numLines; i++) {
        const t = i / numLines;
        const startX = t * width;
        const startY = height;
        
        // Original wavy line logic
        const waveOffset = Math.sin(t * Math.PI * 2) * 20;
        const curveX = width * Math.pow(1 - t, 2) * curvature + waveOffset;
        const curveY = height * (1 - Math.pow(t, 2)) * curvature;
        const endX = curveX;
        const endY = startY - (1 - t) * startY + curveY;
        
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        
        // Gradient fade - use light blue in dark mode
        const gradient = ctx.createLinearGradient(
          endX, endY - 40,
          endX, endY
        );
        if (dark) {
          gradient.addColorStop(0, 'rgba(96, 165, 250, 0.5)');
          gradient.addColorStop(1, 'rgba(96, 165, 250, 0.1)');
        } else if (inverted) {
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0.08)');
        } else {
          gradient.addColorStop(0, 'rgba(2, 45, 84, 0.3)');
          gradient.addColorStop(1, 'rgba(2, 45, 84, 0.08)');
        }
        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(endX, height);
        ctx.stroke();
        
        ctx.strokeStyle = strokeColor;
      }
      
      // Draw square in the center - use light blue in dark mode
      const squareSize = size * 0.167; // ~30px for 180px, scales proportionally
      const squareX = (width - squareSize) / 2;
      const squareY = (height - squareSize) / 2;
      
      const fillColor = dark ? '#60a5fa' : (inverted ? '#ffffff' : '#022d54');
      ctx.fillStyle = fillColor;
      ctx.fillRect(squareX, squareY, squareSize, squareSize);
      
      ctx.restore();
    };

    // Listen for theme changes
    const handleThemeChange = () => {
      drawWavyLines();
    };

    resizeCanvas();
    drawWavyLines();

    // Listen for theme changes
    document.addEventListener('themeChanged', handleThemeChange);
    const observer = new MutationObserver(() => {
      drawWavyLines();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
      document.removeEventListener('themeChanged', handleThemeChange);
      observer.disconnect();
    };
  }, [inverted, size]);

  // Use light blue in dark mode, otherwise use inverted prop
  const isDarkMode = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const strokeColor = isDarkMode ? '#60a5fa' : (inverted ? '#ffffff' : '#022d54');
  const viewBoxSize = 300;
  const logoOffset = (viewBoxSize - size) / 2;

  return (
    <div className={`chatbot-logo-container ${inverted ? 'chatbot-logo-inverted' : ''}`}>
      <svg width={size} height={size} viewBox="0 0 300 300" className="chatbot-logo-svg">
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Canvas for wavy lines - positioned in center */}
        <foreignObject x={logoOffset} y={logoOffset} width={size} height={size}>
          <canvas 
            ref={canvasRef}
            style={{ display: 'block' }}
          />
        </foreignObject>

        {/* Corner brackets - professional framing - spaced further out */}
        <g opacity="0.4">
          <polyline points="20,70 20,20 70,20" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="230,20 280,20 280,70" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="70,280 20,280 20,230" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="280,230 280,280 230,280" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* Subtle outer circle */}
        <circle 
          cx="150" 
          cy="150" 
          r="100" 
          fill="none" 
          stroke={strokeColor} 
          strokeWidth="1" 
          opacity="0.25"
        >
          <animate attributeName="r" values="100;102;100" dur="4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.25;0.4;0.25" dur="4s" repeatCount="indefinite" />
        </circle>

      </svg>
    </div>
  );
}



